// ── "Held for approval" — the maker's side of the gate ───────────────────────
//
// Rendered once, in Layout, and driven by the axios interceptor rather than by
// any page: every write in the app can be held, so a dialog wired per screen
// would be missing from exactly the screens nobody remembered to wire.
//
// Two things it has to do, in this order:
//
//   1. Say what happened without sounding like a refusal. The record is
//      unchanged and the request is already on a manager's queue — "you are not
//      allowed" is the wrong sentence and it is the one an operator hears from
//      a bare red toast.
//   2. Collect the maker's reason. The request exists by the time we ask, so
//      this posts to `/requests/{id}/reason` instead of retrying the save —
//      retrying would hold the change a second time and put two copies of one
//      edit in front of the manager.
//
// The reason is optional. Making it mandatory would strand a pending change
// behind a dialog somebody closed, and the manager can still decide without it.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { API, SUPERVISION_HOLD_EVENT } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';

export default function SupervisionHoldDialog() {
  const [held, setHeld] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onHold = (event) => {
      const data = event?.detail || {};
      if (!data?.supervision?.request_id) return;
      setHeld(data);
      setReason('');
      setSent(false);
    };
    window.addEventListener(SUPERVISION_HOLD_EVENT, onHold);
    return () => window.removeEventListener(SUPERVISION_HOLD_EVENT, onHold);
  }, []);

  const close = useCallback(() => setHeld(null), []);

  const submitReason = useCallback(async () => {
    const text = reason.trim();
    if (!text || !held?.supervision?.request_id) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/supervision/requests/${held.supervision.request_id}/reason`,
        { reason: text },
      );
      setSent(true);
      toast.success('Sent to your manager');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the reason');
    } finally {
      setSaving(false);
    }
  }, [reason, held]);

  if (!held) return null;
  const sup = held.supervision || {};

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            Waiting for approval
          </DialogTitle>
          <DialogDescription>
            {held.message || 'Your change has been sent to your manager.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border bg-slate-50 p-3 text-sm">
            <div className="font-medium text-slate-900">
              {sup.action_label || 'Change'}
              {sup.entity_code ? ` — ${sup.entity_code}` : ''}
            </div>
            {sup.reason && (
              <div className="mt-1 text-slate-600">{sup.reason}</div>
            )}
            {Array.isArray(sup.fields) && sup.fields.length > 0 && (
              <div className="mt-1 text-xs text-slate-500">
                Fields: {sup.fields.join(', ')}
              </div>
            )}
            <div className="mt-2 text-xs text-slate-500">
              Nothing was changed on the record. It will be applied if your
              {sup.stage_label ? ` ${sup.stage_label}` : ''} manager approves it.
            </div>
          </div>

          {sent ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              Your reason has been sent. You can follow it up under My Change Requests.
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">
                Why are you making this change? <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Rate corrected — the slip says Rs 400, the invoice was keyed as Rs 300"
              />
              <p className="text-xs text-slate-500">
                A manager who can see why the edit is needed decides faster than one
                who has to ask.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => { close(); navigate('/my-change-requests'); }}
          >
            View my requests
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={close}>Close</Button>
            {!sent && (
              <Button onClick={submitReason} disabled={saving || !reason.trim()}>
                {saving
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Send className="mr-2 h-4 w-4" />}
                Send reason
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
